import {
  Ban,
  CalendarPlus,
  Check,
  EyeOff,
  ExternalLink,
  Mail,
  Megaphone,
  Pencil,
  RefreshCw,
  Send,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { StatusBadge, btnGhost, btnPrimary, btnSecondary } from '../components/ui';
import { ApiError, type EventReport, type ReportOutcome } from '../lib/api';
import { PROMO_CHANNELS, REPORT_TOPICS, formatStamp, whoLabel } from '../lib/format';
import { useSession } from '../lib/session';

const TABS = [
  ['open', 'Da controllare'],
  ['resolved', 'Risolte'],
  ['dismissed', 'Niente da cambiare'],
] as const;

/** Esiti della correzione: etichetta nel pannello e cosa dice l'email (testo in reports.ts dell'API). */
export const OUTCOMES: Record<ReportOutcome, { label: string; mail: string }> = {
  corrected: {
    label: 'Ho corretto l’evento',
    mail: '«Grazie, abbiamo corretto!» con il link all’evento',
  },
  cancelled: {
    label: 'L’ho segnato come annullato',
    mail: '«Grazie, evento segnato come annullato»: la pagina resta con l’avviso',
  },
  removed: {
    label: 'L’ho tolto dal sito',
    mail: '«Grazie, evento tolto dal sito», senza link all’evento',
  },
  promoted: {
    label: 'Promozione attiva',
    mail: '«La tua richiesta di promozione»: grazie, la promozione è attiva, con il link all’evento',
  },
};

/** Esiti delle correzioni (le richieste di promozione hanno il loro percorso). */
const FIX_OUTCOMES = (Object.keys(OUTCOMES) as ReportOutcome[]).filter((k) => k !== 'promoted');

/**
 * Segnalazioni e richieste dal sito sugli eventi pubblicati. Le richieste di promozione di chi
 * organizza (`promotion`) hanno un percorso a parte: accordarsi, mettere l'evento in evidenza e
 * programmare i post, poi «Promozione attiva» (docs/07-admin-e-bot.md).
 *
 * Problemi segnalati dal sito sugli eventi pubblicati. Per ogni segnalazione aperta: 1) si sistema
 * l'evento (modifica nella scheda, annullato, tolto dal sito), 2) si risponde: l'email a chi ha
 * segnalato spiega cosa abbiamo fatto, con il messaggio della redazione, e ricorda che non si può
 * rispondere (nuova segnalazione o email al contatto). "Niente da cambiare" manda l'email solo
 * con un messaggio. `?id=` apre una segnalazione (link degli avvisi Telegram e ritorno dalla
 * scheda dell'evento, con `outcome=corrected`).
 */
export function Reports() {
  const { api } = useSession();
  const [params] = useSearchParams();
  const focus = params.get('id');
  const [status, setStatus] = useState<(typeof TABS)[number][0]>('open');
  const [items, setItems] = useState<EventReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setItems((await api<{ items: EventReport[] }>(`/reports?status=${status}`)).items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };
  useEffect(() => {
    setItems(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // una segnalazione già chiusa (link vecchio): si cerca nelle altre schede
  useEffect(() => {
    if (!focus || !items || status !== 'open' || items.some((r) => r.id === focus)) return;
    void (async () => {
      for (const s of ['resolved', 'dismissed'] as const) {
        const r = await api<{ items: EventReport[] }>(`/reports?status=${s}`).catch(() => null);
        if (r?.items.some((x) => x.id === focus)) return setStatus(s);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, items]);

  return (
    <main className="mx-auto max-w-[1000px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Segnalazioni e richieste</h1>
        <button onClick={() => void load()} className={btnGhost}>
          <RefreshCw className="size-4" aria-hidden /> Aggiorna
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1" role="tablist">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={status === k}
            onClick={() => setStatus(k)}
            className={`h-9 px-3 text-sm ${status === k ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {items === null ? (
        <p className="mt-4 text-sm text-muted">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-muted">
          {status === 'open' ? 'Nessun problema da controllare.' : 'Nessuna segnalazione.'}
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-3">
          {items.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              focused={r.id === focus}
              initialOutcome={
                r.id === focus ? (params.get('outcome') as ReportOutcome | null) : null
              }
              onClosed={() => setItems((list) => list?.filter((x) => x.id !== r.id) ?? null)}
              onChanged={(next) =>
                setItems((list) => list?.map((x) => (x.id === r.id ? next : x)) ?? null)
              }
            />
          ))}
        </ul>
      )}
    </main>
  );
}

function ReportCard({
  report: r,
  focused,
  initialOutcome,
  onClosed,
  onChanged,
}: {
  report: EventReport;
  focused: boolean;
  initialOutcome: ReportOutcome | null;
  onClosed: () => void;
  onChanged: (r: EventReport) => void;
}) {
  const { api, config } = useSession();
  const promotion = r.topic === 'promotion';
  const [outcome, setOutcome] = useState<ReportOutcome>(
    promotion
      ? 'promoted'
      : initialOutcome && initialOutcome in OUTCOMES
        ? initialOutcome
        : 'corrected',
  );
  const [featuredUntil, setFeaturedUntil] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center' });
  }, [focused]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** Annullato o tolto dal sito (bozza), con le API degli eventi: resta nel registro modifiche. */
  const setEventStatus = (next: 'cancelled' | 'draft') => {
    const question =
      next === 'cancelled'
        ? `Segnare «${r.event.title}» come annullato? La pagina resta sul sito con l’avviso.`
        : `Togliere «${r.event.title}» dal sito? Diventa una bozza: non si vede più, ma puoi ripubblicarlo dalla scheda.`;
    if (!window.confirm(question)) return;
    void run(async () => {
      await api(`/events/${r.event.id}/status`, { method: 'POST', body: { status: next } });
      onChanged({ ...r, eventStatus: next });
      setOutcome(next === 'cancelled' ? 'cancelled' : 'removed');
    });
  };

  /** Richiesta di promozione: l'evento in evidenza fino all'ultimo giorno (sito e social). */
  const feature = () =>
    void run(async () => {
      const res = await api<{ until: string }>(`/reports/${r.id}/feature`, { method: 'POST' });
      setFeaturedUntil(res.until);
    });

  const close = (action: 'resolve' | 'dismiss') =>
    void run(async () => {
      const text = reply.trim();
      await api(`/reports/${r.id}`, {
        method: 'POST',
        body: {
          action,
          ...(action === 'resolve' ? { outcome } : {}),
          ...(text ? { reply: text } : {}),
        },
      });
      onClosed();
    });

  const live = r.eventStatus && r.eventStatus !== 'deleted' ? r.eventStatus : null;
  return (
    <li
      ref={ref}
      className={`flex flex-col gap-2 border p-4 ${focused ? 'border-fg border-2' : 'border-surface-strong'}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          className={`px-2 py-0.5 text-[11px] font-medium uppercase ${r.topic === 'owner_update' || r.topic === 'promotion' ? 'bg-fg text-page' : 'bg-accent-soft'}`}
        >
          {REPORT_TOPICS[r.topic] ?? r.topic}
        </span>
        <Link to={`/events/${r.event.id}`} className="font-medium hover:underline">
          {r.event.title}
        </Link>
        {live ? (
          <StatusBadge status={live} />
        ) : (
          r.eventStatus === 'deleted' && (
            <span className="text-xs text-muted">evento eliminato</span>
          )
        )}
        {(live === 'published' || live === 'cancelled') && (
          <a
            href={`${config.siteUrl}/events/${r.event.slug}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 text-xs text-muted hover:underline"
          >
            sul sito <ExternalLink className="size-3" aria-hidden />
          </a>
        )}
      </div>
      {r.promo && r.promo.length > 0 && (
        <p className="text-sm">
          <span className="font-medium">Promozione richiesta:</span>{' '}
          {r.promo.map((p) => PROMO_CHANNELS[p] ?? p).join(', ')}
        </p>
      )}
      <p className="whitespace-pre-line text-sm">{r.note}</p>
      <p className="text-xs text-muted">
        {formatStamp(r.createdAt)} · {r.contact?.name ? `${r.contact.name} · ` : ''}
        {r.contact?.email ?? 'profilo cancellato'}
        {whoLabel(r.contact) && ` · ${whoLabel(r.contact)}`}
        {r.contact?.phone && (
          <>
            {' · '}
            <a href={`tel:${r.contact.phone.replace(/[^+0-9]/g, '')}`} className="underline">
              tel. {r.contact.phone}
            </a>
          </>
        )}
        {r.closedAt &&
          ` · chiuso ${formatStamp(r.closedAt)}${r.closedBy?.email ? ` da ${r.closedBy.email}` : ''}`}
      </p>
      {r.outcome && <p className="text-sm">Esito: {OUTCOMES[r.outcome].label.toLowerCase()}</p>}
      {r.reply && <p className="bg-surface p-2 text-sm">Messaggio: {r.reply}</p>}

      {r.status === 'open' && promotion && (
        <div className="mt-1 flex flex-col gap-3 border-t border-surface-strong pt-3">
          <section aria-label="Accordati" className="flex flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted">
              1 · Accordati con chi la chiede
            </p>
            <p className="text-sm">
              Proponi cosa fare (evidenza sul sito, post, storie, reel, quando) e, se è a pagamento,
              il prezzo. Si risponde per email o al telefono: il sito non manda la proposta.
            </p>
            {r.contact?.email && (
              <div>
                <a
                  href={`mailto:${r.contact.email}?subject=${encodeURIComponent(`Promozione di «${r.event.title}» su Salento Sagre Eventi`)}`}
                  className={btnSecondary}
                >
                  <Mail className="size-4" aria-hidden /> Scrivi a {r.contact.email}
                </a>
              </div>
            )}
          </section>
          <section aria-label="Prepara la promozione" className="flex flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted">
              2 · Prepara la promozione concordata
            </p>
            <div className="flex flex-wrap gap-2">
              {live === 'published' && (
                <button
                  disabled={busy || !!featuredUntil}
                  onClick={feature}
                  className={btnSecondary}
                >
                  <Star className="size-4" aria-hidden />{' '}
                  {featuredUntil
                    ? `In evidenza fino al ${featuredUntil.split('-').reverse().join('/')}`
                    : 'Metti in evidenza fino all’ultimo giorno'}
                </button>
              )}
              {live === 'published' && (
                <Link to={`/social?event=${r.event.id}`} className={btnSecondary}>
                  <CalendarPlus className="size-4" aria-hidden /> Programma un post sull’evento
                </Link>
              )}
              {live && (
                <Link
                  to={`/events/${r.event.id}?report=${r.id}`}
                  state={{ report: r }}
                  className={btnSecondary}
                >
                  <Pencil className="size-4" aria-hidden /> Modifica evento
                </Link>
              )}
            </div>
            <p className="text-xs text-muted">
              In evidenza: in cima a home ed elenchi del sito con l’etichetta, e sui social la
              scheda «In evidenza» due giorni prima e il conto alla rovescia, in automatico. Post,
              storie e reel in più si programmano nella pagina Social.
            </p>
          </section>
          <section aria-label="Chiudi la richiesta" className="flex flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted">
              3 · Chiudi la richiesta
            </p>
            <textarea
              rows={3}
              maxLength={2000}
              placeholder="Messaggio (facoltativo): per esempio cosa e quando pubblichiamo; va nell’email dopo il testo standard"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              className="w-full border border-surface-strong px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted">
              {r.contact?.email
                ? `«Promozione attiva»: email a ${r.contact.email} con ${OUTCOMES.promoted.mail}${reply.trim() ? ', poi il tuo messaggio' : ''}. «Chiudi senza promozione»: l’email parte solo se scrivi un messaggio.`
                : 'Profilo cancellato: nessuna email.'}
            </p>
            {error && (
              <p role="alert" className="bg-danger-soft p-2 text-sm">
                Errore: {error}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button disabled={busy} onClick={() => close('resolve')} className={btnPrimary}>
                <Megaphone className="size-4" aria-hidden /> Promozione attiva
                {r.contact?.email ? ': invia l’email' : ''}
              </button>
              <button disabled={busy} onClick={() => close('dismiss')} className={btnSecondary}>
                <X className="size-4" aria-hidden /> Chiudi senza promozione
              </button>
            </div>
          </section>
        </div>
      )}

      {r.status === 'open' && !promotion && (
        <div className="mt-1 flex flex-col gap-3 border-t border-surface-strong pt-3">
          <section aria-label="Sistema l’evento" className="flex flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted">
              1 · Sistema l’evento
            </p>
            <div className="flex flex-wrap gap-2">
              {live && (
                <Link
                  to={`/events/${r.event.id}?report=${r.id}`}
                  state={{ report: r }}
                  className={btnSecondary}
                  onClick={() => setOutcome('corrected')}
                >
                  <Pencil className="size-4" aria-hidden /> Modifica evento
                </Link>
              )}
              {live && live !== 'cancelled' && (
                <button
                  disabled={busy}
                  onClick={() => setEventStatus('cancelled')}
                  className={btnSecondary}
                >
                  <Ban className="size-4" aria-hidden /> Segna come annullato
                </button>
              )}
              {live && live !== 'draft' && (
                <button
                  disabled={busy}
                  onClick={() => setEventStatus('draft')}
                  className={btnSecondary}
                >
                  <EyeOff className="size-4" aria-hidden /> Togli dal sito
                </button>
              )}
            </div>
          </section>

          <section aria-label="Rispondi" className="flex flex-col gap-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-muted">
              2 · Rispondi a chi ha segnalato
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm" role="radiogroup">
              {FIX_OUTCOMES.map((k) => (
                <label key={k} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`outcome-${r.id}`}
                    checked={outcome === k}
                    onChange={() => setOutcome(k)}
                  />
                  {OUTCOMES[k].label}
                </label>
              ))}
            </div>
            <textarea
              rows={3}
              maxLength={2000}
              placeholder="Messaggio per chi ha segnalato (facoltativo): va nell’email dopo il testo standard"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              className="w-full border border-surface-strong px-2 py-1 text-sm"
            />
            <p className="text-xs text-muted">
              {r.contact?.email ? (
                <>
                  Email a {r.contact.email}: {OUTCOMES[outcome].mail}
                  {reply.trim() ? ', poi il tuo messaggio' : ''}. In fondo: non si può rispondere,
                  per continuare nuova segnalazione o email a salentosagreeventi@gmail.com.
                </>
              ) : (
                'Profilo cancellato: nessuna email.'
              )}
            </p>
            {error && (
              <p role="alert" className="bg-danger-soft p-2 text-sm">
                Errore: {error}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button disabled={busy} onClick={() => close('resolve')} className={btnPrimary}>
                {r.contact?.email ? (
                  <>
                    <Send className="size-4" aria-hidden /> Chiudi e invia l’email
                  </>
                ) : (
                  <>
                    <Check className="size-4" aria-hidden /> Chiudi
                  </>
                )}
              </button>
              <button
                disabled={busy}
                onClick={() => close('dismiss')}
                className={btnSecondary}
                title="L’evento era giusto: l’email parte solo se scrivi un messaggio"
              >
                <X className="size-4" aria-hidden /> Niente da cambiare
              </button>
            </div>
          </section>
        </div>
      )}
    </li>
  );
}
